// src/pages/sectors/SectorList.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Form,
} from "reactstrap";
import Select from "react-select";
import * as Yup from "yup";
import { useFormik } from "formik";
import toastr from "toastr";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";

import {
  useGetAllSectors,
  useSectorCreate,
  useSectorUpdate,
  useRoleCreate,
  useRoleUpdate,
} from "../../../api/sector";
import { useGetActiveCerts } from "../../../api/certs";
import { useGetAdminConfigUplifts } from "../../../api/price";
import RolePricingBreakdownPreview from "../../../components/Common/RolePricingBreakdownPreview";

// Float matcher for light validation (e.g., 12, 12.3, 12.34)
const FLOAT_RE = /^\d+(\.\d+)?$/;

/** Validated rate string → number for API payloads */
const rateStringToNumber = (rateStr) =>
  parseFloat(String(rateStr ?? "").trim());

const DBS_TYPE_OPTIONS = [
  { value: "basic", label: "Basic DBS" },
  { value: "standard", label: "Standard DBS" },
  { value: "enhanced", label: "Enhanced DBS" },
  { value: "enhancedPlus", label: "Enhanced DBS + Barred List" },
];

const WORKFORCE_OPTIONS = [
  { value: "child", label: "Child Workforce" },
  { value: "adult", label: "Adult Workforce" },
  { value: "both", label: "Both" },
];

const DBS_TYPES_REQUIRING_WORKFORCE = new Set(["enhanced", "enhancedPlus"]);

const isDbsCertSlug = (certSlug, certsCatalog = []) => {
  const slug = String(certSlug ?? "").trim().toLowerCase();
  if (slug === "dbs") return true;
  const meta = (certsCatalog || []).find(
    (c) => String(c?.slug ?? "").trim().toLowerCase() === slug
  );
  return !!meta?.hasServiceNumber;
};

const dbsTypeRequiresWorkforce = (dbsType) =>
  DBS_TYPES_REQUIRING_WORKFORCE.has(String(dbsType ?? "").trim());

const workForceApiToUi = (workForce, dbsType) => {
  if (!dbsTypeRequiresWorkforce(dbsType)) return "";
  if (workForce === "child") return "child";
  if (workForce === "adult") return "adult";
  return "both";
};

const workForceUiToApi = (workForceUi) => {
  if (workForceUi === "child") return "child";
  if (workForceUi === "adult") return "adult";
  if (workForceUi === "both") return null;
  return undefined;
};

const emptyCertConfig = (certSlug, isMandatory = true) => ({
  certSlug: String(certSlug ?? "").trim(),
  isMandatory: !!isMandatory,
  dbsType: "",
  workForceUi: "",
});

const certConfigToApiItem = (cfg, certsCatalog = []) => {
  const certSlug = String(cfg?.certSlug ?? "").trim();
  const item = {
    certSlug,
    isMandatory: !!cfg?.isMandatory,
  };
  if (!isDbsCertSlug(certSlug, certsCatalog)) return item;

  const dbsType = String(cfg?.dbsType ?? "").trim();
  if (dbsType) item.dbsType = dbsType;
  if (dbsTypeRequiresWorkforce(dbsType) && cfg?.workForceUi) {
    item.workForce = workForceUiToApi(cfg.workForceUi);
  }
  return item;
};

const buildCertsApiPayload = (certConfigs, certsCatalog = []) =>
  (Array.isArray(certConfigs) ? certConfigs : [])
    .map((c) => certConfigToApiItem(c, certsCatalog))
    .filter((c) => c.certSlug);

const validateCertConfigs = (certConfigs, certsCatalog = []) => {
  for (const cfg of certConfigs || []) {
    const slug = String(cfg?.certSlug ?? "").trim();
    if (!slug) continue;
    if (!isDbsCertSlug(slug, certsCatalog)) continue;
    const dbsType = String(cfg?.dbsType ?? "").trim();
    if (!dbsType) {
      return "DBS type is required when DBS is assigned to a role.";
    }
    if (dbsTypeRequiresWorkforce(dbsType) && !cfg?.workForceUi) {
      return "Workforce is required for Enhanced or Enhanced Plus DBS.";
    }
  }
  return "";
};

const certSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, backgroundColor: "#fff", zIndex: 9999 }),
  control: (base) => ({ ...base, minHeight: 44 }),
};

const formatCertLabel = (c, certsCatalog = []) => {
  const slug = String(c?.certSlug ?? c?.slug ?? "").trim();
  const title =
    c?.title ||
    certsCatalog.find((x) => String(x.slug) === slug)?.title ||
    slug;
  let label = title;
  if (c?.isMandatory === false) label += " (optional)";
  if (isDbsCertSlug(slug, certsCatalog) && c?.dbsType) {
    const typeLabel =
      DBS_TYPE_OPTIONS.find((o) => o.value === c.dbsType)?.label || c.dbsType;
    label += ` — ${typeLabel}`;
    if (dbsTypeRequiresWorkforce(c.dbsType)) {
      const wf =
        c.workForce === "child"
          ? "Child"
          : c.workForce === "adult"
            ? "Adult"
            : "Both";
      label += ` (${wf})`;
    }
  }
  return label;
};

function RoleCertAssignments({
  certConfigs,
  certificates,
  certSelectOptions,
  onCertConfigsChange,
}) {
  const configs = Array.isArray(certConfigs) ? certConfigs : [];

  const selectValue = configs
    .map((cfg) => {
      const v = String(cfg.certSlug || "").trim();
      if (!v) return null;
      return (
        certSelectOptions.find((o) => o.value === v) || { value: v, label: v }
      );
    })
    .filter(Boolean);

  return (
    <>
      <Select
        isMulti
        options={certSelectOptions}
        value={selectValue}
        onChange={(selected) => {
          const slugs = selected
            ? selected
                .map((opt) => String(opt.value || "").trim())
                .filter(Boolean)
            : [];
          const prev = configs;
          const prevBySlug = new Map(
            prev.map((c) => [String(c.certSlug || "").trim(), c])
          );
          onCertConfigsChange(
            slugs.map((slug) => {
              const existing = prevBySlug.get(slug);
              if (existing) return { ...existing, certSlug: slug };
              return emptyCertConfig(slug, true);
            })
          );
        }}
        placeholder="Select certificates..."
        classNamePrefix="rs"
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        styles={certSelectStyles}
      />
      {configs.length > 0 && (
        <div className="mt-2 small border rounded p-2 bg-light">
          {configs.map((c, i) => {
            const slug = String(c.certSlug || "").trim();
            const title =
              certificates.find((x) => x.slug === slug)?.title || slug;
            const isDbs = isDbsCertSlug(slug, certificates);
            return (
              <div
                key={slug}
                className="border-bottom pb-2 mb-2 last:border-0 last:pb-0 last:mb-0"
              >
                <div className="d-flex align-items-center justify-content-between gap-2 py-1">
                  <span className="text-truncate fw-medium" title={slug}>
                    {title}
                  </span>
                  <div className="d-flex align-items-center gap-3 mb-0">
                    <div className="form-check mb-0">
                      <Input
                        id={`cert-mand-${slug}-${i}`}
                        type="radio"
                        name={`cert-mand-${slug}-${i}`}
                        className="form-check-input"
                        checked={!!c.isMandatory}
                        onChange={() => {
                          const next = [...configs];
                          next[i] = { ...next[i], isMandatory: true };
                          onCertConfigsChange(next);
                        }}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor={`cert-mand-${slug}-${i}`}
                      >
                        Mandatory
                      </Label>
                    </div>
                    <div className="form-check mb-0">
                      <Input
                        id={`cert-opt-${slug}-${i}`}
                        type="radio"
                        name={`cert-mand-${slug}-${i}`}
                        className="form-check-input"
                        checked={!c.isMandatory}
                        onChange={() => {
                          const next = [...configs];
                          next[i] = { ...next[i], isMandatory: false };
                          onCertConfigsChange(next);
                        }}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor={`cert-opt-${slug}-${i}`}
                      >
                        Optional
                      </Label>
                    </div>
                  </div>
                </div>
                {isDbs && (
                  <Row className="g-2 mt-1">
                    <Col md={6}>
                      <Label className="small mb-1">DBS type *</Label>
                      <Input
                        type="select"
                        value={c.dbsType || ""}
                        onChange={(e) => {
                          const dbsType = e.target.value;
                          const next = [...configs];
                          next[i] = {
                            ...next[i],
                            dbsType,
                            workForceUi: dbsTypeRequiresWorkforce(dbsType)
                              ? next[i].workForceUi || ""
                              : "",
                          };
                          onCertConfigsChange(next);
                        }}
                      >
                        <option value="">Select DBS type</option>
                        {DBS_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    {dbsTypeRequiresWorkforce(c.dbsType) && (
                      <Col md={6}>
                        <Label className="small mb-1">Workforce *</Label>
                        <Input
                          type="select"
                          value={c.workForceUi || ""}
                          onChange={(e) => {
                            const next = [...configs];
                            next[i] = {
                              ...next[i],
                              workForceUi: e.target.value,
                            };
                            onCertConfigsChange(next);
                          }}
                        >
                          <option value="">Select workforce</option>
                          {WORKFORCE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                    )}
                  </Row>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// Helper to parse night timings string (e.g., "10pm - 7am" or "22:00 - 07:00") into start and end times
const parseNightTimings = (timingsStr) => {
  if (!timingsStr || !timingsStr.trim()) return { startTime: "", endTime: "" };

  // Try to parse formats like "10pm - 7am" or "22:00 - 07:00"
  const parts = timingsStr.split(/\s*-\s*/).map((s) => s.trim());
  if (parts.length !== 2) return { startTime: "", endTime: "" };

  const [start, end] = parts;

  // Convert 12-hour format to 24-hour format if needed
  const convertTo24Hour = (timeStr) => {
    const trimmed = timeStr.trim().toLowerCase();
    const isPM = trimmed.includes("pm");
    const isAM = trimmed.includes("am");

    if (isPM || isAM) {
      // Extract hour and minute if present (e.g., "10:30pm" or "10pm")
      const timeMatch = trimmed.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/);
      if (timeMatch) {
        let hour = parseInt(timeMatch[1]);
        const minute = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
        if (isPM && hour !== 12) hour += 12;
        if (isAM && hour === 12) hour = 0;
        return `${String(hour).padStart(2, "0")}:${String(minute).padStart(
          2,
          "0"
        )}`;
      }
      // Fallback: just extract number
      const num = parseInt(trimmed.replace(/[^\d]/g, ""));
      if (!isNaN(num)) {
        let hour = num;
        if (isPM && hour !== 12) hour += 12;
        if (isAM && hour === 12) hour = 0;
        return `${String(hour).padStart(2, "0")}:00`;
      }
    }

    // Already in 24-hour format (HH:MM or HH:MM:SS)
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
      const [h, m] = trimmed.split(":");
      return `${String(parseInt(h)).padStart(2, "0")}:${String(
        parseInt(m)
      ).padStart(2, "0")}`;
    }

    return "";
  };

  return {
    startTime: convertTo24Hour(start) || "",
    endTime: convertTo24Hour(end) || "",
  };
};

// Helper to format times into night timings string
const formatNightTimings = (startTime, endTime) => {
  if (!startTime || !endTime) return "";

  const formatTo12Hour = (time24) => {
    const [h, m] = time24.split(":");
    const hour = parseInt(h);
    const minute = parseInt(m) || 0;
    const period = hour >= 12 ? "pm" : "am";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}${
      minute > 0 ? `:${String(minute).padStart(2, "0")}` : ""
    }${period}`;
  };

  return `${formatTo12Hour(startTime)} - ${formatTo12Hour(endTime)}`;
};

/** Sector create: POST admin/sector/create — { title, nightTimings } */
const CREATE_SCHEMA = Yup.object({
  title: Yup.string().trim().required("Title is required"),
  nightTimings: Yup.string().trim().required("Night timings are required"),
});

/** Sector update: PATCH admin/sector/update/:slug */
const UPDATE_SCHEMA = Yup.object({
  title: Yup.string().trim().required("Title is required"),
  nightTimings: Yup.string().trim().required("Night timings are required"),
  isActive: Yup.boolean().required(),
});

const buildSectorCreatePayload = (values) => ({
  title: values.title?.trim() ?? "",
  nightTimings: values.nightTimings?.trim() ?? "",
});

const buildSectorUpdatePayload = (values) => ({
  title: values.title?.trim() ?? "",
  nightTimings: values.nightTimings?.trim() ?? "",
  isActive: !!values.isActive,
});

/** One item in POST admin/roles/create body.roles[] */
const buildRoleCreateItem = (roleValues, certsCatalog = []) => {
  const title = String(roleValues.title ?? "").trim();
  const rate = String(roleValues.workerBaseRate ?? "").trim();
  const marginStr = String(roleValues.marginPercentage ?? "").trim();
  const out = {
    title,
    workerBaseRate: rateStringToNumber(rate),
    marginPercentage: rateStringToNumber(marginStr),
    drivingDocsRequired: !!roleValues.drivingDocsRequired,
  };
  const desc = String(roleValues.description ?? "").trim();
  if (desc) out.description = desc;
  const certs = buildCertsApiPayload(roleValues.certConfigs, certsCatalog);
  if (certs.length > 0) out.certs = certs;
  return out;
};

const createEmptyRoleRow = () => ({
  title: "",
  workerBaseRate: "",
  marginPercentage: "",
  drivingDocsRequired: false,
  description: "",
  certConfigs: [],
});

const createEmptyRoleErrors = () => ({
  title: "",
  workerBaseRate: "",
  marginPercentage: "",
  description: "",
});

/** Normalize role.certs[] or legacy certIds[] into cert config rows for create/edit forms */
function buildCertConfigsFromRole(role, certsCatalog = []) {
  if (!role) return [];
  if (Array.isArray(role.certs) && role.certs.length > 0) {
    return role.certs
      .map((c) => {
        const direct = String(c?.slug ?? c?.certSlug ?? "").trim();
        const certSlug = direct
          ? direct
          : (() => {
              const id = c?.certId ?? c?.id;
              if (id == null || !certsCatalog.length) return "";
              const meta = certsCatalog.find((x) => String(x.id) === String(id));
              return meta?.slug ? String(meta.slug).trim() : "";
            })();
        if (!certSlug) return null;
        const dbsType = String(c?.dbsType ?? "").trim();
        return {
          certSlug,
          isMandatory: !!c?.isMandatory,
          dbsType,
          workForceUi: workForceApiToUi(c?.workForce, dbsType),
        };
      })
      .filter(Boolean);
  }
  if (
    Array.isArray(role.certIds) &&
    role.certIds.length > 0 &&
    Array.isArray(certsCatalog) &&
    certsCatalog.length > 0
  ) {
    return role.certIds
      .map((id) => {
        const meta = certsCatalog.find((x) => String(x.id) === String(id));
        return meta?.slug ? emptyCertConfig(meta.slug, true) : null;
      })
      .filter(Boolean);
  }
  return [];
}

const SectorList = () => {
  document.title = "Sectors & Roles | JobArk";

  // server-side controls
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // sector modals
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);

  // roles viewer modal
  const [showRoles, setShowRoles] = useState(false);
  const [rolesFor, setRolesFor] = useState(null); // sector object

  // NEW: create roles modal
  const [showCreateRoles, setShowCreateRoles] = useState(false);
  const [createRolesFor, setCreateRolesFor] = useState(null); // sector object for which we create roles

  // NEW: edit single role modal
  const [showEditRole, setShowEditRole] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // role obj being edited

  // Night timings time pickers
  const [nightStartTime, setNightStartTime] = useState("");
  const [nightEndTime, setNightEndTime] = useState("");

  // queries/mutations
  const { mutate, data, isLoading, isPending, error } = useGetAllSectors();
  const createSector = useSectorCreate();
  const updateSector = useSectorUpdate();

  const roleCreate = useRoleCreate();
  const roleUpdate = useRoleUpdate();

  // Certificates
  const getCerts = useGetActiveCerts();
  const [certificates, setCertificates] = useState([]);
  const upliftsQuery = useGetAdminConfigUplifts();
  const adminUplifts = useMemo(
    () => upliftsQuery.data?.data ?? upliftsQuery.data ?? null,
    [upliftsQuery.data]
  );

  const loading = isLoading || isPending;

  // Fetch certificates on mount
  useEffect(() => {
    getCerts.mutate(
      { showDBS: true },
      {
        onSuccess: (response) => {
          const certs = response?.data?.data || response?.data || [];
          setCertificates(certs);
        },
        onError: () => {
          setCertificates([]);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mutate({
      page,
      limit,
      filter: filter === "all" ? "" : filter,
      search: search.trim() || undefined,
    });
  }, [page, limit, filter, search, mutate]);

  const payload = data?.data ?? data;
  const sectors = payload?.sectors ?? [];
  const total = payload?.total ?? 0;

  // CREATE sector form
  const createFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: "",
      nightTimings: "",
    },
    validationSchema: CREATE_SCHEMA,
    onSubmit: (values) => {
      const timings =
        values.nightTimings ||
        formatNightTimings(nightStartTime, nightEndTime) ||
        "";
      const payload = buildSectorCreatePayload({
        ...values,
        nightTimings: timings,
      });
      createSector.mutate(payload, {
        onSuccess: () => {
          toastr.success("Sector created");
          setShowCreate(false);
          createFormik.resetForm();
          setNightStartTime("");
          setNightEndTime("");
          mutate({
            page,
            limit,
            filter: filter === "all" ? "" : filter,
            search: search.trim() || undefined,
          });
        },
        onError: () => {},
      });
    },
  });

  // UPDATE sector form
  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: editing?.title || "",
      nightTimings: editing?.nightTimings || "",
      isActive: !!editing?.isActive,
    },
    validationSchema: UPDATE_SCHEMA,
    onSubmit: (values) => {
      if (!editing?.slug) return;
      let timings = formatNightTimings(nightStartTime, nightEndTime);
      if (!timings) {
        timings = values.nightTimings || "";
      }

      const latestValues = { ...values, nightTimings: timings };
      const payload = buildSectorUpdatePayload(latestValues);
      if (!editing?.slug) return;
      updateSector.mutate(
        { slug: editing.slug, ...payload },
        {
          onSuccess: () => {
            toastr.success("Sector updated");
            setShowEdit(false);
            setEditing(null);
            setNightStartTime("");
            setNightEndTime("");
            mutate({
              page,
              limit,
              filter: filter === "all" ? "" : filter,
              search: search.trim() || undefined,
            });
          },
          onError: () => {},
        }
      );
    },
  });

  // Parse night timings when editing changes or modal opens
  useEffect(() => {
    if (showEdit && editing?.nightTimings) {
      const parsed = parseNightTimings(editing.nightTimings);
      setNightStartTime(parsed.startTime);
      setNightEndTime(parsed.endTime);
      // Also update formik value for validation
      const timings = formatNightTimings(parsed.startTime, parsed.endTime);
      if (timings && editFormik) {
        editFormik.setFieldValue("nightTimings", timings);
      }
    } else if (!showEdit) {
      // Reset when modal closes
      setNightStartTime("");
      setNightEndTime("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, showEdit]);

  // Helper to update night timings in formik
  const updateNightTimings = (startTime, endTime, formik) => {
    setNightStartTime(startTime);
    setNightEndTime(endTime);
    const timings = formatNightTimings(startTime, endTime);
    formik.setFieldValue("nightTimings", timings);
    formik.setFieldTouched("nightTimings", true);
  };

  const columns = useMemo(
    () => [
      { header: "Title", accessorKey: "title", enableColumnFilter: false },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue() ? (
            <Badge className="bg-success">Active</Badge>
          ) : (
            <Badge className="bg-secondary">Inactive</Badge>
          ),
      },
      {
        header: "Roles",
        accessorKey: "roles",
        enableColumnFilter: false,
        cell: (info) =>
          Array.isArray(info.row.original.roles)
            ? info.row.original.roles.length
            : 0,
      },
      {
        header: "Created At",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue()).toLocaleDateString()
            : "-",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const row = cellProps.row.original;
          return (
            <div className="d-flex gap-2">
              <Button
                size="sm"
                color="soft-info"
                onClick={() => {
                  setEditing(row);
                  setShowEdit(true);
                }}
              >
                <i className="mdi mdi-pencil-outline me-1" />
                Edit
              </Button>

              {/* View Roles */}
              <Button
                size="sm"
                color="soft-primary"
                onClick={() => {
                  setRolesFor(row);
                  setShowRoles(true);
                }}
              >
                <i className="mdi mdi-account-multiple-outline me-1" />
                Roles
              </Button>

              {/* NEW: Create Role(s) for this sector */}
              <Button
                size="sm"
                color="soft-success"
                onClick={() => {
                  setCreateRolesFor(row);
                  setShowCreateRoles(true);
                }}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create Roles
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  // roles derived
  const roles = Array.isArray(rolesFor?.roles) ? rolesFor.roles : [];

  // ============ Create Roles Modal (bulk) ============
  const [newRoles, setNewRoles] = useState([createEmptyRoleRow()]);
  const [newRoleErrors, setNewRoleErrors] = useState([createEmptyRoleErrors()]);

  useEffect(() => {
    if (showCreateRoles) {
      setNewRoles([createEmptyRoleRow()]);
      setNewRoleErrors([createEmptyRoleErrors()]);
    }
  }, [showCreateRoles]);

  const addRoleRow = () => {
    setNewRoles((r) => [...r, createEmptyRoleRow()]);
    setNewRoleErrors((errs) => [...errs, createEmptyRoleErrors()]);
  };
  const removeRoleRow = (idx) => {
    setNewRoles((r) => (r.length === 1 ? r : r.filter((_, i) => i !== idx)));
    setNewRoleErrors((errs) =>
      errs.length === 1 ? errs : errs.filter((_, i) => i !== idx)
    );
  };

  const handleRoleChange = (idx, field, value) => {
    setNewRoles((r) =>
      r.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );
    setNewRoleErrors((errs) =>
      errs.map((err, i) => {
        if (i !== idx || !(field in err)) return err;
        return { ...err, [field]: "" };
      })
    );
  };

  const submitCreateRoles = () => {
    const sectorSlug = createRolesFor?.slug;
    if (!sectorSlug) return;

    const errors = newRoles.map(() => createEmptyRoleErrors());
    let hasErrors = false;

    for (let i = 0; i < newRoles.length; i++) {
      const { title, workerBaseRate, marginPercentage, description } =
        newRoles[i];
      const t = String(title || "").trim();
      const rate = String(workerBaseRate ?? "").trim();
      const margin = String(marginPercentage ?? "").trim();
      if (!t) {
        errors[i].title = "Title is required";
        hasErrors = true;
      }
      if (!rate) {
        errors[i].workerBaseRate = "Worker base rate (£) is required";
        hasErrors = true;
      } else if (!FLOAT_RE.test(rate)) {
        errors[i].workerBaseRate =
          "Worker base rate must be a valid number (e.g., 16.68)";
        hasErrors = true;
      }
      if (!margin) {
        errors[i].marginPercentage = "Margin percentage is required";
        hasErrors = true;
      } else if (!FLOAT_RE.test(margin)) {
        errors[i].marginPercentage =
          "Margin percentage must be a valid number";
        hasErrors = true;
      } else {
        const m = parseFloat(margin);
        if (m < 15 || m > 20) {
          errors[i].marginPercentage =
            "Margin percentage must be between 15 and 20";
          hasErrors = true;
        }
      }
      const d = String(description ?? "").trim();
      if (d.length > 2000) {
        errors[i].description = "Description is too long";
        hasErrors = true;
      }
    }

    for (let i = 0; i < newRoles.length; i++) {
      const certMsg = validateCertConfigs(newRoles[i].certConfigs, certificates);
      if (certMsg) {
        toastr.error(`Role ${i + 1}: ${certMsg}`);
        hasErrors = true;
      }
    }

    setNewRoleErrors(errors);
    if (hasErrors) {
      return;
    }

    const rolesPayload = newRoles
      .map((role) => {
        const item = buildRoleCreateItem(role, certificates);
        return item.title.length > 0 ? item : null;
      })
      .filter(Boolean);

    roleCreate.mutate(
      { sectorSlug, roles: rolesPayload },
      {
        onSuccess: () => {
          toastr.success("Roles created");
          setShowCreateRoles(false);
          // refresh list
          mutate({
            page,
            limit,
            filter: filter === "all" ? "" : filter,
            search: search.trim() || undefined,
          });
        },
        onError: () => {},
      }
    );
  };

  // ============ Edit Role Modal ============
  const editRoleInitialValues = useMemo(
    () => ({
      title: editingRole?.title || "",
      description: editingRole?.description || "",
      workerBaseRate:
        editingRole?.workerBaseRate !== undefined &&
        editingRole?.workerBaseRate !== null
          ? String(editingRole.workerBaseRate)
          : "",
      marginPercentage:
        editingRole?.marginPercentage !== undefined &&
        editingRole?.marginPercentage !== null
          ? String(editingRole.marginPercentage)
          : "",
      isActive: editingRole?.isActive !== false,
      drivingDocsRequired: !!editingRole?.drivingDocsRequired,
      certConfigs: buildCertConfigsFromRole(editingRole, certificates),
    }),
    [editingRole, certificates]
  );

  const editRoleFormik = useFormik({
    enableReinitialize: true,
    initialValues: editRoleInitialValues,
    validationSchema: Yup.object({
      title: Yup.string().trim().required("Title is required"),
      description: Yup.string().trim().max(2000),
      workerBaseRate: Yup.string()
        .trim()
        .required("Worker base rate (£) is required")
        .test(
          "is-float",
          "Worker base rate must be a valid number (e.g., 16.68)",
          (v) => FLOAT_RE.test(v || "")
        ),
      marginPercentage: Yup.string()
        .trim()
        .required("Margin percentage is required")
        .test(
          "margin-range",
          "Margin percentage must be between 15 and 20",
          (v) => {
            if (!FLOAT_RE.test(v || "")) return false;
            const n = parseFloat(v);
            return !Number.isNaN(n) && n >= 15 && n <= 20;
          }
        ),
      isActive: Yup.boolean().required(),
      drivingDocsRequired: Yup.boolean().required(),
      certConfigs: Yup.array().of(
        Yup.object({
          certSlug: Yup.string().required(),
          isMandatory: Yup.boolean(),
          dbsType: Yup.string(),
          workForceUi: Yup.string(),
        })
      ),
    }),
    onSubmit: (vals) => {
      if (!editingRole?.slug) return;

      const certMsg = validateCertConfigs(vals.certConfigs, certificates);
      if (certMsg) {
        toastr.error(certMsg);
        return;
      }

      const rateStr = String(vals.workerBaseRate ?? "").trim();
      const rateNum = rateStringToNumber(rateStr);
      const marginStr = String(vals.marginPercentage ?? "").trim();
      const marginNum = rateStringToNumber(marginStr);
      roleUpdate.mutate(
        {
          slug: editingRole.slug,
          title: vals.title.trim(),
          description: String(vals.description ?? "").trim(),
          isActive: !!vals.isActive,
          drivingDocsRequired: !!vals.drivingDocsRequired,
          workerBaseRate: rateNum,
          weekDayPrice: rateNum,
          marginPercentage: marginNum,
          certs: buildCertsApiPayload(vals.certConfigs, certificates),
        },
        {
          onSuccess: () => {
            toastr.success("Role updated");
            setShowEditRole(false);
            setEditingRole(null);
            setShowRoles(false);
            mutate({
              page,
              limit,
              filter: filter === "all" ? "" : filter,
              search: search.trim() || undefined,
            });
          },
          onError: () => {},
        }
      );
    },
  });

  const roleCertSelectOptions = useMemo(
    () =>
      (certificates || [])
        .filter((c) => c?.slug)
        .map((c) => ({
          value: String(c.slug).trim(),
          label: c.title || c.slug,
        })),
    [certificates]
  );

  const editRoleCertSelectOptions = useMemo(() => {
    const byVal = new Map(roleCertSelectOptions.map((o) => [o.value, o]));
    (editRoleFormik.values.certConfigs || []).forEach((cfg) => {
      const v = String(cfg.certSlug || "").trim();
      if (v && !byVal.has(v)) {
        byVal.set(v, { value: v, label: v });
      }
    });
    return Array.from(byVal.values());
  }, [roleCertSelectOptions, editRoleFormik.values.certConfigs]);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Sectors" breadcrumbItem="Sectors List" />

        <Row className="mb-3 g-2 align-items-center">
          {/* Search */}
          <Col md={4}>
            <Input
              type="text"
              placeholder="Search by title..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
            />
          </Col>

          <Col md={2}>
            <Input
              type="select"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Input>
          </Col>
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button
              color="primary"
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              disabled={loading}
            >
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setSearch("");
                setSearchInput("");
                setFilter("all");
                setLimit(10);
                setPage(1);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            {/* Page size */}
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button color="primary" onClick={() => setShowCreate(true)}>
              <i className="mdi mdi-plus me-1" />
              New Sector
            </Button>
            <Button
              color="light"
              onClick={() =>
                mutate({
                  page,
                  limit,
                  filter: filter === "all" ? "" : filter,
                  search: search.trim() || undefined,
                })
              }
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Sectors</h5>
          </CardHeader>
          <CardBody>
            {loading && sectors.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load sectors.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={sectors}
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Create Sector */}
      <Modal
        isOpen={showCreate}
        toggle={() => {
          setShowCreate((v) => !v);
          setNightStartTime("");
          setNightEndTime("");
        }}
      >
        <ModalHeader toggle={() => setShowCreate((v) => !v)}>
          Create Sector
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            createFormik.handleSubmit();
          }}
        >
          <ModalBody>
            <Row className="g-3">
              <Col md={12}>
                <Label for="create-title">Title</Label>
                <Input
                  id="create-title"
                  name="title"
                  value={createFormik.values.title}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    !!(createFormik.touched.title && createFormik.errors.title)
                  }
                  placeholder="e.g., Healthcare & Social Care"
                />
                {createFormik.touched.title && createFormik.errors.title && (
                  <div className="invalid-feedback d-block">
                    {createFormik.errors.title}
                  </div>
                )}
              </Col>

              <Col md={12}>
                <Label>Night timings</Label>
                <Row className="g-2">
                  <Col md={6}>
                    <Label
                      for="create-nightStartTime"
                      className="small text-muted"
                    >
                      Start Time
                    </Label>
                    <Input
                      id="create-nightStartTime"
                      type="time"
                      value={nightStartTime}
                      onChange={(e) =>
                        updateNightTimings(
                          e.target.value,
                          nightEndTime,
                          createFormik
                        )
                      }
                      invalid={
                        !!(
                          createFormik.touched.nightTimings &&
                          createFormik.errors.nightTimings
                        )
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Label
                      for="create-nightEndTime"
                      className="small text-muted"
                    >
                      End Time
                    </Label>
                    <Input
                      id="create-nightEndTime"
                      type="time"
                      value={nightEndTime}
                      onChange={(e) =>
                        updateNightTimings(
                          nightStartTime,
                          e.target.value,
                          createFormik
                        )
                      }
                      invalid={
                        !!(
                          createFormik.touched.nightTimings &&
                          createFormik.errors.nightTimings
                        )
                      }
                    />
                  </Col>
                </Row>
                {createFormik.touched.nightTimings &&
                  createFormik.errors.nightTimings && (
                    <div className="invalid-feedback d-block">
                      {createFormik.errors.nightTimings}
                    </div>
                  )}
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setShowCreate(false)}
              disabled={createSector.isPending}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={createSector.isPending}
            >
              {createSector.isPending ? "Creating..." : "Create"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Edit Sector */}
      <Modal
        isOpen={showEdit}
        toggle={() => {
          setShowEdit((v) => !v);
          setNightStartTime("");
          setNightEndTime("");
        }}
      >
        <ModalHeader toggle={() => setShowEdit((v) => !v)}>
          Update Sector
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            editFormik.handleSubmit();
          }}
        >
          <ModalBody>
            <Row className="g-3">
              <Col md={12}>
                <Label for="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  name="title"
                  value={editFormik.values.title}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  invalid={
                    !!(editFormik.touched.title && editFormik.errors.title)
                  }
                />
                {editFormik.touched.title && editFormik.errors.title && (
                  <div className="invalid-feedback d-block">
                    {editFormik.errors.title}
                  </div>
                )}
              </Col>

              <Col md={12}>
                <Label>Night timings</Label>
                <Row className="g-2">
                  <Col md={6}>
                    <Label
                      for="edit-nightStartTime"
                      className="small text-muted"
                    >
                      Start Time
                    </Label>
                    <Input
                      id="edit-nightStartTime"
                      type="time"
                      value={nightStartTime}
                      onChange={(e) =>
                        updateNightTimings(
                          e.target.value,
                          nightEndTime,
                          editFormik
                        )
                      }
                      invalid={
                        !!(
                          editFormik.touched.nightTimings &&
                          editFormik.errors.nightTimings
                        )
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Label for="edit-nightEndTime" className="small text-muted">
                      End Time
                    </Label>
                    <Input
                      id="edit-nightEndTime"
                      type="time"
                      value={nightEndTime}
                      onChange={(e) =>
                        updateNightTimings(
                          nightStartTime,
                          e.target.value,
                          editFormik
                        )
                      }
                      invalid={
                        !!(
                          editFormik.touched.nightTimings &&
                          editFormik.errors.nightTimings
                        )
                      }
                    />
                  </Col>
                </Row>
                {editFormik.touched.nightTimings &&
                  editFormik.errors.nightTimings && (
                    <div className="invalid-feedback d-block">
                      {editFormik.errors.nightTimings}
                    </div>
                  )}
              </Col>

              <Col md={12}>
                <div className="form-check mt-2">
                  <Input
                    id="edit-active"
                    type="checkbox"
                    className="form-check-input"
                    name="isActive"
                    checked={editFormik.values.isActive}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                  />
                  <Label className="form-check-label" htmlFor="edit-active">
                    Active
                  </Label>
                  {editFormik.touched.isActive &&
                    editFormik.errors.isActive && (
                      <div className="invalid-feedback d-block">
                        {String(editFormik.errors.isActive)}
                      </div>
                    )}
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setShowEdit(false)}
              disabled={updateSector.isPending}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={updateSector.isPending}
            >
              {updateSector.isPending ? "Updating..." : "Update"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Roles Modal (read-only list + Edit buttons) */}
      <Modal
        isOpen={showRoles}
        toggle={() => setShowRoles((v) => !v)}
        size="lg"
      >
        <ModalHeader toggle={() => setShowRoles((v) => !v)}>
          Roles — {rolesFor?.title || "-"}
        </ModalHeader>
        <ModalBody>
          {roles.length === 0 ? (
            <div className="text-muted">No roles found for this sector.</div>
          ) : (
            <div className="list-group">
              {roles.map((r) => (
                <div
                  key={r.id}
                  className="list-group-item d-flex justify-content-between align-items-start"
                >
                  <div>
                    <div className="fw-semibold text-capitalize">{r.title}</div>
                    {r.workerBaseRate != null && r.workerBaseRate !== "" && (
                      <div className="small mt-1">
                        Worker base rate £{String(r.workerBaseRate)}
                      </div>
                    )}
                    {r.marginPercentage != null && r.marginPercentage !== "" && (
                      <div className="small mt-1">
                        Margin {String(r.marginPercentage)}%
                      </div>
                    )}
                    {r.drivingDocsRequired ? (
                      <div className="small mt-1 text-muted">
                        Driving docs required
                      </div>
                    ) : null}
                    {Array.isArray(r.certs) && r.certs.length > 0 && (
                      <div className="small mt-1">
                        Certs:{" "}
                        {r.certs
                          .map((c) =>
                            formatCertLabel(
                              {
                                ...c,
                                certSlug: c.certSlug || c.slug,
                              },
                              certificates
                            )
                          )
                          .join(", ")}
                      </div>
                    )}
                    {r.description && (
                      <div className="small mt-1">{r.description}</div>
                    )}
                    {Array.isArray(r.conditionalFields) &&
                      r.conditionalFields.length > 0 && (
                        <div className="small text-muted mt-1">
                          Conditional:{" "}
                          {r.conditionalFields.map((f) => f.label).join(", ")}
                        </div>
                      )}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {r.isActive ? (
                      <Badge className="bg-success">Active</Badge>
                    ) : (
                      <Badge className="bg-secondary">Inactive</Badge>
                    )}
                    <Button
                      size="sm"
                      color="soft-info"
                      onClick={() => {
                        setEditingRole(r);
                        setShowEditRole(true);
                      }}
                    >
                      <i className="mdi mdi-pencil-outline" /> Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowRoles(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create Roles (bulk) */}
      <Modal
        isOpen={showCreateRoles}
        toggle={() => setShowCreateRoles((v) => !v)}
        size="xl"
      >
        <ModalHeader toggle={() => setShowCreateRoles((v) => !v)}>
          Create Roles — {createRolesFor?.title || "-"}
        </ModalHeader>
        <ModalBody>
          {newRoles.map((row, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Role {idx + 1}</h6>
              </div>
              <Row className="g-2">
                <Col md={6}>
                  <Label>Title *</Label>
                  <Input
                    value={row.title}
                    placeholder="e.g., Domestic Cleaner"
                    onChange={(e) =>
                      handleRoleChange(idx, "title", e.target.value)
                    }
                    invalid={!!newRoleErrors[idx]?.title}
                  />
                  {newRoleErrors[idx]?.title && (
                    <div className="invalid-feedback d-block">
                      {newRoleErrors[idx]?.title}
                    </div>
                  )}
                </Col>
                <Col md={6}>
                  <Label>Worker base rate (£) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    value={row.workerBaseRate}
                    placeholder="e.g., 16.68"
                    onChange={(e) =>
                      handleRoleChange(idx, "workerBaseRate", e.target.value)
                    }
                    invalid={!!newRoleErrors[idx]?.workerBaseRate}
                  />
                  {newRoleErrors[idx]?.workerBaseRate && (
                    <div className="invalid-feedback d-block">
                      {newRoleErrors[idx]?.workerBaseRate}
                    </div>
                  )}
                </Col>
                <Col md={6}>
                  <Label>Margin percentage (%) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="15"
                    max="20"
                    inputMode="decimal"
                    value={row.marginPercentage}
                    placeholder="15–20"
                    onChange={(e) =>
                      handleRoleChange(idx, "marginPercentage", e.target.value)
                    }
                    invalid={!!newRoleErrors[idx]?.marginPercentage}
                  />
                  {newRoleErrors[idx]?.marginPercentage && (
                    <div className="invalid-feedback d-block">
                      {newRoleErrors[idx]?.marginPercentage}
                    </div>
                  )}
                </Col>
              </Row>

              <RolePricingBreakdownPreview
                workerBaseRate={row.workerBaseRate}
                marginPercentage={row.marginPercentage}
                uplifts={adminUplifts}
              />

              <Row className="g-2 mt-2">
                <Col md={12}>
                  <div className="form-check">
                    <Input
                      id={`driving-docs-${idx}`}
                      type="checkbox"
                      className="form-check-input"
                      checked={!!row.drivingDocsRequired}
                      onChange={(e) =>
                        handleRoleChange(
                          idx,
                          "drivingDocsRequired",
                          e.target.checked
                        )
                      }
                    />
                    <Label
                      className="form-check-label"
                      htmlFor={`driving-docs-${idx}`}
                    >
                      Driving documents required
                    </Label>
                  </div>
                </Col>
                <Col md={12}>
                  <Label>Description (optional)</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={row.description || ""}
                    placeholder="Optional notes for this role"
                    onChange={(e) =>
                      handleRoleChange(idx, "description", e.target.value)
                    }
                    invalid={!!newRoleErrors[idx]?.description}
                  />
                  {newRoleErrors[idx]?.description && (
                    <div className="invalid-feedback d-block">
                      {newRoleErrors[idx]?.description}
                    </div>
                  )}
                </Col>
              </Row>

              <Row className="g-2 mt-1">
                <Col md={12}>
                  <Label>Certificates</Label>
                  <RoleCertAssignments
                    certConfigs={row.certConfigs || []}
                    certificates={certificates}
                    certSelectOptions={roleCertSelectOptions}
                    onCertConfigsChange={(next) =>
                      handleRoleChange(idx, "certConfigs", next)
                    }
                  />
                </Col>
              </Row>

              <div className="mt-3 d-flex justify-content-between">
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => removeRoleRow(idx)}
                  disabled={newRoles.length === 1}
                >
                  Remove
                </Button>
                {idx === newRoles.length - 1 && (
                  <Button color="secondary" size="sm" onClick={addRoleRow}>
                    Add another
                  </Button>
                )}
              </div>
            </div>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowCreateRoles(false)}
            disabled={roleCreate.isPending}
          >
            Cancel
          </Button>
          <Button
            color="success"
            onClick={submitCreateRoles}
            disabled={roleCreate.isPending}
          >
            {roleCreate.isPending ? "Creating..." : "Create Roles"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Role */}
      <Modal
        isOpen={showEditRole}
        toggle={() => setShowEditRole((v) => !v)}
        size="xl"
      >
        <ModalHeader toggle={() => setShowEditRole((v) => !v)}>
          Edit Role — {editingRole?.title || "-"}
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            editRoleFormik.handleSubmit();
          }}
        >
          <ModalBody>
            <Row className="g-3">
              <Col md={6}>
                <Label>Title *</Label>
                <Input
                  name="title"
                  value={editRoleFormik.values.title}
                  onChange={editRoleFormik.handleChange}
                  onBlur={editRoleFormik.handleBlur}
                  invalid={
                    !!(
                      editRoleFormik.touched.title &&
                      editRoleFormik.errors.title
                    )
                  }
                />
                {editRoleFormik.touched.title &&
                  editRoleFormik.errors.title && (
                    <div className="invalid-feedback d-block">
                      {editRoleFormik.errors.title}
                    </div>
                  )}
              </Col>
              <Col md={6}>
                <Label>Worker base rate (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  name="workerBaseRate"
                  placeholder="e.g., 16.68"
                  value={editRoleFormik.values.workerBaseRate}
                  onChange={editRoleFormik.handleChange}
                  onBlur={editRoleFormik.handleBlur}
                  invalid={
                    !!(
                      editRoleFormik.touched.workerBaseRate &&
                      editRoleFormik.errors.workerBaseRate
                    )
                  }
                />
                {editRoleFormik.touched.workerBaseRate &&
                  editRoleFormik.errors.workerBaseRate && (
                    <div className="invalid-feedback d-block">
                      {editRoleFormik.errors.workerBaseRate}
                    </div>
                  )}
              </Col>
              <Col md={6}>
                <Label>Margin percentage (%) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="15"
                  max="20"
                  inputMode="decimal"
                  name="marginPercentage"
                  placeholder="15–20"
                  value={editRoleFormik.values.marginPercentage}
                  onChange={editRoleFormik.handleChange}
                  onBlur={editRoleFormik.handleBlur}
                  invalid={
                    !!(
                      editRoleFormik.touched.marginPercentage &&
                      editRoleFormik.errors.marginPercentage
                    )
                  }
                />
                {editRoleFormik.touched.marginPercentage &&
                  editRoleFormik.errors.marginPercentage && (
                    <div className="invalid-feedback d-block">
                      {editRoleFormik.errors.marginPercentage}
                    </div>
                  )}
              </Col>
              <Col md={12}>
                <RolePricingBreakdownPreview
                  workerBaseRate={editRoleFormik.values.workerBaseRate}
                  marginPercentage={editRoleFormik.values.marginPercentage}
                  uplifts={adminUplifts}
                />
              </Col>
              <Col md={12}>
                <Label>Description</Label>
                <Input
                  type="textarea"
                  rows={2}
                  name="description"
                  value={editRoleFormik.values.description}
                  onChange={editRoleFormik.handleChange}
                  onBlur={editRoleFormik.handleBlur}
                  invalid={
                    !!(
                      editRoleFormik.touched.description &&
                      editRoleFormik.errors.description
                    )
                  }
                />
                {editRoleFormik.touched.description &&
                  editRoleFormik.errors.description && (
                    <div className="invalid-feedback d-block">
                      {editRoleFormik.errors.description}
                    </div>
                  )}
              </Col>
              <Col md={6}>
                <div className="form-check mt-2">
                  <Input
                    id="edit-role-active"
                    type="checkbox"
                    className="form-check-input"
                    name="isActive"
                    checked={editRoleFormik.values.isActive}
                    onChange={editRoleFormik.handleChange}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="edit-role-active"
                  >
                    Active
                  </Label>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-check mt-2">
                  <Input
                    id="edit-role-driving"
                    type="checkbox"
                    className="form-check-input"
                    name="drivingDocsRequired"
                    checked={editRoleFormik.values.drivingDocsRequired}
                    onChange={editRoleFormik.handleChange}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="edit-role-driving"
                  >
                    Driving documents required
                  </Label>
                </div>
              </Col>
            </Row>
            <Row className="g-2 mt-3">
              <Col md={12}>
                <Label>Certificates</Label>
                <RoleCertAssignments
                  certConfigs={editRoleFormik.values.certConfigs || []}
                  certificates={certificates}
                  certSelectOptions={editRoleCertSelectOptions}
                  onCertConfigsChange={(next) =>
                    editRoleFormik.setFieldValue("certConfigs", next)
                  }
                />
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setShowEditRole(false)}
              disabled={roleUpdate.isPending}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={roleUpdate.isPending}
            >
              {roleUpdate.isPending ? "Updating..." : "Update Role"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default SectorList;
